<template>
    <div class="container">
        <div v-if="loading">
        </div>
        <div v-else>
            <div v-if="object">
                <div role="button" class="small" @click="goBack()"><i
                        class="fa-solid fa-chevron-left m-2 mt-3"></i>{{ $t('common.back') }}
                </div>
                <h3 class="my-3">{{ object.name }}</h3>

                <div class="accordion shadow mt-3" id="accordionDescription">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': !object.description }"
                                class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseDescription" aria-expanded="false"
                                aria-controls="collapseDescription">
                                {{ $t('browse.description') }}
                            </button>
                        </h2>
                        <div id="collapseDescription" class="accordion-collapse collapse"
                            data-bs-parent="#accordionDescription">
                            <div class="accordion-body">
                                <div v-if="object.description" class="small text-multiline text-break">
                                    {{ object.description }}
                                </div>
                                <div v-else class="fst-italic small">
                                    {{ $t('taskDetail.noDescription') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="accordion shadow mt-3" id="accordionNotes">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': !object.notes }"
                                class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseNotes" aria-expanded="false"
                                aria-controls="collapseNotes">
                                {{ $t('browse.notes') }}
                            </button>
                        </h2>
                        <div id="collapseNotes" class="accordion-collapse collapse" data-bs-parent="#accordionNotes">
                            <div class="accordion-body">
                                <div v-if="object.notes" class="small text-multiline text-break">
                                    {{ object.notes }}
                                </div>
                                <div v-else class="fst-italic small">
                                    {{ $t('browse.noNotes') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="accordion shadow mt-3" id="accordionNeedsAttention">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': object.needs_attention.length == 0 }"
                                class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseNeedsAttention" aria-expanded="false"
                                aria-controls="collapseNeedsAttention">
                                {{ $t('browse.deviceIssues') }}
                            </button>
                        </h2>
                        <div id="collapseNeedsAttention" class="accordion-collapse collapse"
                            data-bs-parent="#accordionNeedsAttention">
                            <div class="accordion-body">
                                <ul v-if="object.needs_attention.length > 0" class="list-group list-group-flush">
                                    <li v-for="item in object.needs_attention" :key="item.id"
                                        class="my-2 list-group-item">
                                        <router-link :to="`/tasks/${item.id}`"
                                            class="text-decoration-none d-flex justify-content-between align-items-start">
                                            <div>
                                                <div class="small fw-bolder">{{ item.name }}</div>
                                                <div class="small my-1">{{ item.notes }}</div>
                                                <div class="text-secondary"><small>{{ item.finished_by_name }} {{
                                                    item.finished_on ? formatTimestamp(item.finished_on) : ''
                                                        }}</small></div>
                                            </div>
                                            <div><i class="ms-2 fa-solid fa-triangle-exclamation"
                                                    :class="{ 'text-danger': item.needs_attention == 1, 'text-warning': item.needs_attention == 2, 'text-primary': item.needs_attention == 4 }"></i>
                                            </div>
                                        </router-link>
                                    </li>
                                </ul>
                                <div v-else class="fst-italic small">{{ $t('taskDetail.noIssues') }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="accordion shadow mt-3" id="accordionFiles">
                    <div class="accordion-item border-0">
                        <h2 class="accordion-header">
                            <button :class="{ 'text-body-tertiary': object.files.length == 0 }"
                                class="accordion-button collapsed fs-6 fw-bolder" type="button"
                                data-bs-toggle="collapse" data-bs-target="#collapseFiles" aria-expanded="false"
                                aria-controls="collapseFiles">
                                {{ $t('taskDetail.attachments') }}
                            </button>
                        </h2>
                        <div id="collapseFiles" class="accordion-collapse collapse" data-bs-parent="#accordionFiles">
                            <div class="accordion-body">
                                <ul v-if="object.files.length > 0" class="list-unstyled">
                                    <li class="my-2 list-group-item d-flex justify-content-between align-items-start"
                                        v-for="file in object.files" :key="file.id">
                                        <div class="btn btn-light w-100 text-start text-truncate"
                                            @click="requestFile(file)"><i
                                                class="fa-solid fa-paperclip me-2"></i><small>{{
                                                file.filename }}</small></div>
                                    </li>
                                </ul>
                                <div v-else class="fst-italic small">{{ $t('taskDetail.noAttachments') }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-3">
                    <div v-if="!object.has_guid" class="col text-center">
                        <button class="btn btn-primary" @click="openQRScanner">{{ $t('browse.linkQr') }}</button>
                    </div>
                    <div class="col text-center">
                        <router-link :to="{ name: 'ObjectTasks', params: { objectId: object.id } }">
                            <button class="btn btn-primary">{{ $t('browse.viewTasks') }}</button>
                        </router-link>
                    </div>
                    <div class="col text-center">
                        <router-link :to="{ name: 'ObjectMaintenanceLog', params: { objectId: object.id } }">
                            <button class="btn btn-primary">{{ $t('maintenanceLog.title') }}</button>
                        </router-link>
                    </div>
                </div>

                <div v-if="qrScannerVisible" class="qr-scanner-modal">
                    <div class="qr-scanner-content">
                        <qrcode-stream @detect="onDetect" @init="onInit" :formats="['qr_code']" />
                        <button @click="closeQRScanner" class="btn btn-primary mt-3">{{ $t('common.close') }}</button>
                    </div>
                </div>

            </div>
            <NotFound v-else :item="$t('browse.objectItem')" />
        </div>
    </div>
</template>

<script>
import { useFormatTimestamp } from '@/composables/useFormatTimestamp';
import axios from '@/services/axios';
import { setDocumentTitle } from '@/composables/usePageTitle';
import { downloadBlob } from '@/utils/downloadFile';
import NotFound from '@/components/NotFound.vue';
import { QrcodeStream } from 'vue-qrcode-reader';

export default {
    data() {
        return {
            object: null,
            loading: true,
            qrScannerVisible: false,
            // The scanner keeps emitting detections while the camera is open, so
            // without this one QR code could fire several POSTs before the first
            // resolves and the scanner is torn down.
            qrProcessing: false,
        };
    },
    components: {
        NotFound,
        QrcodeStream
    },
    setup() {
        const { formatTimestamp } = useFormatTimestamp();
        return { formatTimestamp };
    },
    methods: {
        async fetchObject() {
            try {
                const objectId = this.$route.params.objectId;
                const response = await axios.get(`/objects/${objectId}`);
                this.object = response.data.data;
                setDocumentTitle(this.object?.name);
            } catch (error) {
                console.error('Error fetching object details:', error);
                // Optionally, handle the error (e.g., set an error message, redirect, etc.)
            } finally {
                this.loading = false;
            }
        },
        async requestFile(file) {
            try {
                const response = await axios({
                    url: `objects/${this.object.id}/files/${file.id}`, // API endpoint to get the file
                    method: 'GET',
                    responseType: 'blob', // Important for files
                });

                downloadBlob(response.data, file.filename);
            } catch (error) {
                console.error('Error fetching file:', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('taskDetail.attachmentFailed'), 'danger');
            }
        },
        goBack() {
            if (this.$route.meta.internalNavigation && window.history.length > 2) {
                this.$router.go(-1);
            } else {
                this.$router.push('/');
            }
        },
        openQRScanner() {
            this.qrScannerVisible = true;
        },
        closeQRScanner() {
            this.qrScannerVisible = false;
        },
        onInit(promise) {
            promise.catch(error => {
                console.error('QR code reader initialization failed:', error);
            });
        },
        async onDetect(decodedArray) {
            if (this.qrProcessing) return;
            this.qrProcessing = true;
            try {
                console.log(decodedArray)
                if (decodedArray.length > 0 && decodedArray[0].rawValue) {
                    const url = new URL(decodedArray[0].rawValue);
                    const urlParams = new URLSearchParams(url.search);
                    const linkValue = urlParams.get('link');

                    if (linkValue) {
                        const objectId = this.$route.params.objectId;
                        await axios.post(`/objects/${objectId}/qr`, { link: linkValue });
                        this.fetchObject();
                    } else {
                        alert(this.$t('browse.invalidQr'));
                    }
                }
            } catch (error) {
                console.error('Failed to process QR code:', error);
                alert(this.$t('browse.qrProcessFailed'));
            } finally {
                this.qrScannerVisible = false;
                this.qrProcessing = false;
            }
        },
    },
    mounted() {
        this.fetchObject();
    }
};
</script>
<style scoped>
.qr-scanner-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.qr-scanner-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    text-align: center;
}
</style>